import React, { useEffect, useState } from 'react';
import {
  joinWaitlist,
  updateWaitlistDetails,
} from '../../services/waitlistService';
import { lookupReferrer, type ResolvedReferrer } from '../../services/referralAttributionService';
import useReferralCode from '../../hooks/useReferralCode';
import { WAITLIST_COPY } from '../../marketing/content/waitlist';
import { trackWaitlistSignup } from '../../utils/trackWaitlistSignup';
import { markWaitlistJoined } from '../../utils/waitlistJoined';
import WaitlistDepositBlock from './WaitlistDepositBlock';
import {
  MAX_REFERRAL_RAW_LENGTH,
  WAITLIST_FOCUS_AREAS,
  WAITLIST_FOCUS_AREA_LABELS,
  WAITLIST_TIMINGS,
  WAITLIST_TIMING_LABELS,
  type WaitlistFocusArea,
  type WaitlistInterest,
  type WaitlistCaptureSurface,
  type WaitlistTiming,
} from '../../types/waitlist';

/**
 * Shared waitlist capture used by every placement slot (W-2).
 *
 * TWO THINGS THAT LOOK LIKE DETAILS BUT ARE THE DESIGN:
 *
 * 1. It submits IN PLACE. No route change, no redirect to a form page. A slot
 *    that links out to a form loses roughly half the people who intended to
 *    finish, and the marketing site has no other cheap conversion.
 *
 * 2. It is THREE STEPS. Email + name + consent bank first; then the
 *    deposit (ReservePlaceButton → Stripe Checkout); the optional qualifiers
 *    (outward code, timing, household) are asked on the thank-you page after
 *    payment (`initialPhase="details"` + `entryRef`), where abandoning them
 *    costs nothing.
 *
 * 3. THE REFERRAL BOX IS ON STEP 1, not on the optional questions. Step 1 is
 *    the only step everybody reaches — the optional questions sit behind a
 *    deposit payment and a "Skip" button, and attribution collected there would
 *    systematically miss exactly the people whose referrer most needs
 *    crediting. One short optional field is the price of that.
 *
 * Melio is pre-launch and in collect-only mode, so the copy must not imply
 * imminent contact — see the success state below.
 */

/**
 * 'panel' is the Health Check drawer (src/marketing/health-check): one column,
 * no step 2, and the caller decides what to show after success.
 * 'popup' is the join-the-waitlist popup (src/marketing/popup): name, email
 * and consent only. No referral box, though a confirmed `?ref=` link still
 * shows with its "Not right? Change".
 */
export type WaitlistFormVariant = 'band' | 'inline' | 'footer' | 'panel' | 'popup';

interface WaitlistFormProps {
  sourceSurface: WaitlistCaptureSurface;
  variant?: WaitlistFormVariant;
  /** Prefill when the surrounding page already knows who this is (W-5). */
  initialEmail?: string;
  initialFirstName?: string;
  /**
   * Set when the CTA that linked here already implies an assessment, e.g.
   * "Register interest in Plus" on /assessments. Sent silently with the join;
   * it is no longer asked as a question anywhere. Step 2's slot went to the
   * areas-of-interest question instead, which tells the team what to staff
   * for in far more detail than Core-or-Plus ever did.
   */
  initialInterest?: WaitlistInterest | null;
  /** Replaces "Request an introduction" on the submit button. */
  submitLabel?: string;
  /** Called once the entry is saved, with the server ref (null when deduplicated). */
  onSuccess?: (ref: string | null) => void;
  /** Replaces the default success copy. */
  doneBody?: React.ReactNode;
  /** 'details' renders the optional questions for an existing, paid entry (the thank-you page). */
  initialPhase?: 'collect' | 'details';
  /** The entry the details belong to, when starting at 'details'. */
  entryRef?: string | null;
  /**
   * Overrides the `?ref=` code this form would otherwise read from the URL.
   * Only a caller that already knows the referrer should set it; every normal
   * placement leaves it undefined and lets useReferralCode do the work.
   */
  referrerCode?: string | null;
  /**
   * 'inline' (default) shows the deposit step in place after a save.
   * 'navigate' skips it and hands the ref to onSuccess, for callers that move
   * the visitor to /waitlist/deposit instead (the popup, which must never run
   * Stripe inside itself).
   */
  afterSave?: 'inline' | 'navigate';
}

type Phase = 'collect' | 'pay' | 'details' | 'done';

export const CONSENT_TEXT =
  "Email me about Melio membership. We'll only write about places opening and " +
  'the practice itself—never more than monthly, and you can unsubscribe in ' +
  'one click.';

const WaitlistForm: React.FC<WaitlistFormProps> = ({
  sourceSurface,
  variant = 'band',
  initialEmail = '',
  initialFirstName = '',
  initialInterest = null,
  submitLabel,
  onSuccess,
  doneBody,
  initialPhase = 'collect',
  entryRef: initialEntryRef = null,
  referrerCode,
  afterSave = 'inline',
}) => {
  const [phase, setPhase] = useState<Phase>(initialPhase);
  const [email, setEmail] = useState(initialEmail);
  const [firstName, setFirstName] = useState(initialFirstName);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entryRef, setEntryRef] = useState<string | null>(initialEntryRef);

  // Who sent them. `linkedReferrer` is the resolved `?ref=` code; `referredBy`
  // is whatever they typed. Exactly one of the two is in play at a time.
  const [referredBy, setReferredBy] = useState('');
  const [linkedReferrer, setLinkedReferrer] = useState<ResolvedReferrer | null>(null);
  // `?ref=` from wherever the colleague's link pointed — the footer form on a
  // content page counts just as much as /waitlist itself.
  const urlReferrerCode = useReferralCode();
  const activeReferrerCode = referrerCode !== undefined ? referrerCode : urlReferrerCode;

  const [outwardCode, setOutwardCode] = useState('');
  const [timing, setTiming] = useState<WaitlistTiming | null>(null);
  const [household, setHousehold] = useState<boolean | null>(null);
  // Multi-select: people are usually interested in more than one thing, and a
  // single pick would force a ranking the visitor has no reason to have made.
  const [focusAreas, setFocusAreas] = useState<WaitlistFocusArea[]>([]);

  const toggleFocusArea = (area: WaitlistFocusArea) =>
    setFocusAreas((current) =>
      current.includes(area) ? current.filter((a) => a !== area) : [...current, area],
    );

  // Resolve the link's code to a name, purely so we can show "Referred by
  // Jonathan Davies" rather than a code. Attribution itself is decided
  // server-side from the raw value, so a failed lookup costs nothing but the
  // confirmation line — the code is still sent and can still match.
  useEffect(() => {
    if (!activeReferrerCode) {
      setLinkedReferrer(null);
      return;
    }
    let cancelled = false;
    lookupReferrer(activeReferrerCode).then((found) => {
      if (!cancelled) setLinkedReferrer(found);
    });
    return () => {
      cancelled = true;
    };
  }, [activeReferrerCode]);

  const handleJoin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    if (!email.trim() || !firstName.trim()) {
      setError('Please add your name and email.');
      return;
    }
    if (!consent) {
      setError('Please tick the box so we know we can write to you.');
      return;
    }

    setSubmitting(true);
    setError(null);

    const result = await joinWaitlist({
      email: email.trim(),
      firstName: firstName.trim(),
      consentMarketingEmail: consent,
      sourceSurface,
      interest: initialInterest,
      // A confirmed link wins over the box; the box is only rendered when
      // there is no confirmed link, so the two cannot both be set.
      referredBy: linkedReferrer ? linkedReferrer.code : referredBy.trim() || null,
      referralMethod: linkedReferrer ? 'link' : referredBy.trim() ? 'typed' : null,
    });

    setSubmitting(false);

    if (!result.success) {
      // Keep everything they typed. Losing a typed email to a network blip is
      // the one failure mode this form must never have.
      setError(result.error || 'We could not save that just now. Please try again.');
      return;
    }

    setEntryRef(result.ref ?? null);
    // The Google Ads conversion. Saved, and before any payment step.
    if (result.ref) trackWaitlistSignup(result.ref, sourceSurface);
    // Joined through any form: nothing on the site needs to ask again.
    markWaitlistJoined(sourceSurface);

    if (afterSave === 'navigate' && result.ref) {
      onSuccess?.(result.ref);
      return;
    }
    // The deposit comes next, on every surface. Without a ref there is nothing
    // to pay against, so fall through to the confirmation.
    setPhase(result.ref ? 'pay' : 'done');
    onSuccess?.(result.ref ?? null);
  };

  const handleDetails = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!entryRef) {
      setPhase('done');
      return;
    }
    setSubmitting(true);
    await updateWaitlistDetails({
      ref: entryRef,
      outwardCode: outwardCode.trim() || null,
      timing,
      household,
      focusAreas: focusAreas.length ? focusAreas : null,
    });
    setSubmitting(false);
    setPhase('done');
  };

  // ----------------------------------------------------------------- pay ---
  if (phase === 'pay' && entryRef) {
    return <WaitlistDepositBlock entryRef={entryRef} />;
  }

  // ---------------------------------------------------------------- done ---
  if (phase === 'done') {
    return (
      <div className="wl-done" role="status" aria-live="polite">
        <p className="gh-eyebrow mb-3">{initialPhase === 'details' ? WAITLIST_COPY.reserved : WAITLIST_COPY.onTheList}</p>
        {doneBody ?? (
          <p className="wl-done__body">
            Thank you{firstName.trim() ? `, ${firstName.trim()}` : ''}. We haven't opened to new
            members yet—when we do, we'll write to you before anyone else.
          </p>
        )}
      </div>
    );
  }

  // ------------------------------------------------------------- details ---
  if (phase === 'details') {
    return (
      <form className="wl-form wl-form--details" onSubmit={handleDetails}>
        <p className="gh-eyebrow mb-3">{WAITLIST_COPY.reserved}</p>
        <p className="wl-details__intro">
          Four optional questions—they help us decide where and when to open
          and what to staff for first. You can skip them.
        </p>

        <div className="wl-field">
          <label className="gh-input-label" htmlFor={`wl-outward-${sourceSurface}`}>
            Where are you based?
          </label>
          <input
            id={`wl-outward-${sourceSurface}`}
            className="gh-input"
            type="text"
            autoComplete="postal-code"
            placeholder="SW1"
            value={outwardCode}
            onChange={(e) => setOutwardCode(e.target.value)}
          />
          <p className="wl-help">
            Just the first half of your postcode—we use it to plan where we
            open next, not to identify your address.
          </p>
        </div>

        <div className="wl-field">
          <span className="gh-input-label">How soon were you thinking?</span>
          <div className="wl-choices" role="group" aria-label="Timing">
            {WAITLIST_TIMINGS.map((option) => (
              <button
                key={option}
                type="button"
                className="wl-choice"
                data-selected={timing === option ? 'true' : 'false'}
                onClick={() => setTiming(timing === option ? null : option)}
              >
                {WAITLIST_TIMING_LABELS[option]}
              </button>
            ))}
          </div>
        </div>

        <div className="wl-field">
          <span className="gh-input-label">Joining alongside someone?</span>
          <div className="wl-choices" role="group" aria-label="Household">
            <button
              type="button"
              className="wl-choice"
              data-selected={household === true ? 'true' : 'false'}
              onClick={() => setHousehold(household === true ? null : true)}
            >
              Yes, a partner or family member
            </button>
            <button
              type="button"
              className="wl-choice"
              data-selected={household === false ? 'true' : 'false'}
              onClick={() => setHousehold(household === false ? null : false)}
            >
              Just me
            </button>
          </div>
        </div>

        {/* Service interest, deliberately NOT a health goal. "Which areas of
            Melio interest you" is a preference about what we offer; "what do
            you want to improve" would be a statement about their health, and
            that is special-category data this form must never collect. The
            wording is load-bearing — see WaitlistFocusArea before changing it. */}
        <div className="wl-field">
          <span className="gh-input-label" id={`wl-focus-label-${sourceSurface}`}>
            Which areas of Melio interest you most?
          </span>
          <div
            className="wl-choices"
            role="group"
            aria-labelledby={`wl-focus-label-${sourceSurface}`}
          >
            {WAITLIST_FOCUS_AREAS.map((area) => (
              <button
                key={area}
                type="button"
                className="wl-choice"
                aria-pressed={focusAreas.includes(area)}
                data-selected={focusAreas.includes(area) ? 'true' : 'false'}
                onClick={() => toggleFocusArea(area)}
              >
                {WAITLIST_FOCUS_AREA_LABELS[area]}
              </button>
            ))}
          </div>
          <p className="wl-help">Pick as many as you like.</p>
        </div>

        <div className="wl-actions">
          <button type="submit" className="gh-btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save these'}
          </button>
          <button
            type="button"
            className="wl-skip"
            onClick={() => setPhase('done')}
          >
            Skip
          </button>
        </div>
      </form>
    );
  }

  // ------------------------------------------------------------- collect ---
  return (
    <form
      className={`wl-form wl-form--${variant}`}
      onSubmit={handleJoin}
      noValidate
    >
      <div className="wl-fields">
        <div className="wl-field">
          <label className="gh-input-label" htmlFor={`wl-name-${sourceSurface}`}>
            First name
          </label>
          <input
            id={`wl-name-${sourceSurface}`}
            className="gh-input"
            type="text"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="wl-field">
          <label className="gh-input-label" htmlFor={`wl-email-${sourceSurface}`}>
            Email
          </label>
          <input
            id={`wl-email-${sourceSurface}`}
            className="gh-input"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            aria-describedby={error ? `wl-error-${sourceSurface}` : undefined}
          />
        </div>
      </div>

      {/* Who sent them. Optional, and never validated in the browser: an
          unrecognised name still gets stored verbatim and attributed by hand
          in ops, because telling a visitor their friend "isn't recognised" is
          both rude and wrong — the registry is our gap, not theirs. */}
      {/* The popup has no room for the box, but a confirmed link is still
          shown so the visitor can see who gets the credit and clear it. */}
      {variant === 'popup' && !linkedReferrer ? null : linkedReferrer ? (
        <div className="wl-referral wl-referral--linked" role="status">
          <p className="wl-referral__confirmed">
            Referred by <strong>{linkedReferrer.displayName}</strong>
          </p>
          <button
            type="button"
            className="wl-skip"
            onClick={() => setLinkedReferrer(null)}
            disabled={submitting}
          >
            Not right? Change
          </button>
        </div>
      ) : (
        <div className="wl-field wl-referral">
          <label className="gh-input-label" htmlFor={`wl-referral-${sourceSurface}`}>
            Did someone tell you about Melio? <span className="wl-optional">Optional</span>
          </label>
          <input
            id={`wl-referral-${sourceSurface}`}
            className="gh-input"
            type="text"
            autoComplete="off"
            placeholder="Their name, or the code they gave you"
            maxLength={MAX_REFERRAL_RAW_LENGTH}
            value={referredBy}
            onChange={(e) => setReferredBy(e.target.value)}
            disabled={submitting}
            aria-describedby={`wl-referral-help-${sourceSurface}`}
          />
          <p className="wl-help" id={`wl-referral-help-${sourceSurface}`}>
            So we can thank them.
          </p>
        </div>
      )}

      {/* Unticked by default and unbundled from anything else. UK PECR soft
          opt-in does not apply — there is no prior sale to rely on. */}
      <label className="wl-consent">
        <span className="gh-consent-row__check" data-checked={consent} aria-hidden="true">
          <svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2 6.5 L 5 9 L 10 3"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <span className="wl-consent__text">{CONSENT_TEXT}</span>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
        />
      </label>

      {/* Article 13 wants this AT the point of collection, not one click into
          a footer. Opens in a new tab so a half-typed form is not lost. */}
      <p className="wl-help" style={{ marginTop: '0.5rem' }}>
        Your name, your email and—if you tell us—who sent you. Nothing about your health.{' '}
        <a
          href="/privacy#waitlist"
          className="gh-link"
          target="_blank"
          rel="noopener noreferrer"
        >
          How we handle your details
        </a>
        .
      </p>

      {error && (
        <p className="wl-error" id={`wl-error-${sourceSurface}`} role="alert">
          {error}
        </p>
      )}

      <div className="wl-actions">
        <button type="submit" className="gh-btn-primary" disabled={submitting}>
          {submitting ? 'Just a moment…' : submitLabel ?? 'Request an introduction'}
        </button>
      </div>
    </form>
  );
};

export default WaitlistForm;
